// MuseIQ - Loop Logic Module
// The active loop (ActiveLoop) and the measure-to-beat helpers the loop handles use

import type { Score, Beat } from './alphatab-types.js';
import type { model } from '../lib/alphaTab';
import type { LoopTicks } from './shared/loop-ticks.js';
import { measureAt, beatStartTick, beatEndTick, inLoop, sameLoop } from './shared/loop-ticks.js';
import type { SavedLoop } from './components/controls/saved-loops.js';

// ============================================
// Types
// ============================================

/**
 * The active loop's range, as ActiveLoop hands it out. A LoopRegion is a
 * LoopTicks (display ticks, end exclusive), so the passes and the graying take
 * it as is. It carries no ms: conductor's loopRegionRecord works them out.
 */
export interface LoopRegion extends LoopTicks {
    /** First and last measure (inclusive), from the master bars: same on every track. */
    startMeasureIndex: number;
    endMeasureIndex: number;
    /** Earliest in-loop sound. Below startTick when a before-beat grace note is drawn on the first beat. */
    playbackStartTick: number;
    /** Where playback stops: the first out-of-loop onset or the end of the last in-loop beat, whichever is earlier. */
    playbackEndTick: number;
}

// ============================================
// Measure Helpers
// ============================================

/**
 * Get the first and last beats for a given measure index.
 * Uses the specified track (default 0), staff 0 as the reference.
 */
export function getBeatsForMeasure(score: Score, measureIndex: number, trackIndex: number = 0): { startBeat: Beat; endBeat: Beat } | null {
    const bars = score.tracks[trackIndex]?.staves[0]?.bars;
    if (!bars || measureIndex < 0 || measureIndex >= bars.length) {
        console.warn(`[Loop] Invalid measure index: ${measureIndex}, total bars: ${bars?.length ?? 0}`);
        return null;
    }

    const bar = bars[measureIndex];
    const voices = bar.voices;
    if (!voices || voices.length === 0) return null;

    const primaryVoice = voices[0];
    const startBeat = primaryVoice.beats[0];
    if (!startBeat) return null;

    const endBeat = primaryVoice.beats[primaryVoice.beats.length - 1];
    if (!endBeat) return null;

    return { startBeat, endBeat };
}

/**
 * Get total number of measures in the score.
 */
export function getMeasureCount(score: Score, trackIndex: number = 0): number {
    return score.tracks[trackIndex]?.staves[0]?.bars?.length ?? 0;
}

/**
 * Get a specific Beat by measure and beat index (voice 0).
 */
export function getBeatAtIndex(score: Score, measureIndex: number, beatIndex: number, trackIndex: number = 0): Beat | null {
    const bars = score.tracks[trackIndex]?.staves[0]?.bars;
    if (!bars || measureIndex < 0 || measureIndex >= bars.length) return null;
    const beats = bars[measureIndex].voices[0]?.beats;
    if (!beats || beatIndex < 0 || beatIndex >= beats.length) return null;
    return beats[beatIndex];
}

/**
 * Get the last beat index (0-based) for a given measure.
 */
export function getLastBeatIndex(score: Score, measureIndex: number, trackIndex: number = 0): number {
    const bars = score.tracks[trackIndex]?.staves[0]?.bars;
    if (!bars || measureIndex < 0 || measureIndex >= bars.length) return 0;
    const beats = bars[measureIndex].voices[0]?.beats;
    return beats ? beats.length - 1 : 0;
}

// ============================================
// ActiveLoop
// ============================================

export type LoopHandle = 'start' | 'end';

/** What each event carries. Listeners pick the events they care about; the cause is which one fired. */
export interface ActiveLoopEvents {
    /**
     * Looping turned on at the cursor, or a saved loop picked (also while another
     * loop is on: listeners replace whatever loop they had). savedLoopId is null
     * for the toggle; the scroll-to-loop listener uses it.
     */
    activated: { region: LoopRegion; savedLoopId: number | null };
    /**
     * Live preview: a drag moved a handle to a different range. Graying, the range
     * highlight and the handles listen. The passes don't.
     */
    dragged: { region: LoopRegion; handle: LoopHandle };
    /**
     * A drag finished. Always fired, after `dragged` if the drop changed the range.
     * Both passes listen and call setLoop, which is a no-op when sameLoop says
     * the drag changed nothing.
     */
    dragEnded: { region: LoopRegion; handle: LoopHandle };
    /** Looping turned off: the toggle, leaving a mode that loops, or a new score. */
    deactivated: Record<string, never>;
}

type Listener<K extends keyof ActiveLoopEvents> = (event: ActiveLoopEvents[K]) => void;

/** The fields a create or update request sends. `name` is set when the name should change. */
export interface SavedLoopFields extends LoopTicks {
    startMeasureIndex: number;
    endMeasureIndex: number;
    name?: string;
}

/**
 * Label for a saved loop's measure range, e.g. "Measures 2–3" (1-based): the
 * name a new saved loop gets. The <saved-loops> rows show it too. ActiveLoop
 * compares a stored name against it to tell an auto-named loop from one the
 * user renamed, so the row label and the auto-name rule must stay this one
 * function.
 */
export function formatLoopRange(startMeasureIndex: number, endMeasureIndex: number): string {
    return `Measures ${startMeasureIndex + 1}–${endMeasureIndex + 1}`;
}

/**
 * The loop the player is using right now, or none. Owns whether looping is on,
 * the range (display ticks), snapping and clamping, the playback range, and
 * which saved loop the range came from. No DOM, playback or HTTP: conductor
 * calls it on user actions and score loads, and reacts to its events.
 */
export class ActiveLoop {
    private _score: model.Score | null = null;
    private _region: LoopRegion | null = null;
    private _savedLoopId: number | null = null;
    private _savedTicks: LoopTicks | null = null;
    private _listeners: { [K in keyof ActiveLoopEvents]?: Listener<K>[] } = {};

    /** Subscribe. Listeners run in order, synchronously; one that throws fails the call that caused it. */
    on<K extends keyof ActiveLoopEvents>(name: K, listener: Listener<K>): () => void {
        const list = (this._listeners[name] ??= []) as Listener<K>[];
        list.push(listener);
        return () => {
            const i = list.indexOf(listener);
            if (i >= 0) list.splice(i, 1);
        };
    }

    /** The active loop's range, or null when looping is off. */
    get region(): LoopRegion | null {
        return this._region;
    }

    /** The saved loop the active loop came from, even after a drag (the "Update" target). */
    get savedLoopId(): number | null {
        return this._savedLoopId;
    }

    /** The saved loop's id only while the range is still exactly what was picked or saved. Stamped on records. */
    get unchangedSavedLoopId(): number | null {
        return this._savedLoopId !== null && sameLoop(this._region, this._savedTicks) ? this._savedLoopId : null;
    }

    /** A new score is ready (playback ticks filled in). Turns the loop off. */
    scoreReady(score: model.Score): void {
        this._score = score;
        this.turnOff();
    }

    /**
     * Turn looping on over the bar the player hears at a playback tick (the
     * cursor). That's the bar of the latest beat started at or before the tick,
     * so a tick on a before-beat grace note picks the grace note's bar, not the
     * bar it sounds in. False if there is no score.
     */
    turnOn(atTick: number): boolean {
        if (!this._score) return false;
        if (this._region) return true;
        const m = this._barHeardAt(atTick);
        if (m < 0) return false;
        const bar = this._score.masterBars[m];
        this._activate({ startTick: bar.start, endTick: bar.start + bar.calculateDuration() }, null);
        return true;
    }

    turnOff(): void {
        this._savedLoopId = null;
        this._savedTicks = null;
        if (!this._region) return;
        this._region = null;
        this._emit('deactivated', {});
    }

    /** Make a saved loop the active loop. False if it doesn't fit this score. */
    pick(saved: SavedLoop): boolean {
        if (!this._score) return false;
        const ticks = { startTick: saved.startTick, endTick: saved.endTick };
        if (!this._fits(ticks)) return false;
        this._activate(ticks, saved.id);
        return true;
    }

    /** A handle was dragged onto a beat (any track). Start stays before end. */
    drag(handle: LoopHandle, beat: model.Beat): void {
        if (!this._region || !this._score) return;
        const ticks = this._dragTicks(handle, beat);
        if (sameLoop(ticks, this._region)) return;
        this._region = this._regionFor(ticks);
        this._emit('dragged', { region: this._region, handle });
    }

    /** A drag finished, on a beat, or on nothing (dropped where no beat resolves). */
    endDrag(handle: LoopHandle, beat: model.Beat | null): void {
        if (!this._region) return;
        if (beat) this.drag(handle, beat);
        this._emit('dragEnded', { region: this._region!, handle });
    }

    /** Fields for saving the active loop as a new saved loop, auto-named. Null when off. */
    newSavedLoop(): (SavedLoopFields & { name: string }) | null {
        const fields = this._fields();
        if (!fields) return null;
        return { ...fields, name: formatLoopRange(fields.startMeasureIndex, fields.endMeasureIndex) };
    }

    /**
     * Fields for overwriting a saved loop with the active range. Renames it only
     * if it's auto-named. Null when off.
     */
    updatedSavedLoop(existing: SavedLoop | undefined): SavedLoopFields | null {
        const fields = this._fields();
        if (!fields) return null;
        const autoNamed = existing !== undefined
            && existing.name === formatLoopRange(existing.startMeasureIndex, existing.endMeasureIndex);
        return autoNamed ? { ...fields, name: formatLoopRange(fields.startMeasureIndex, fields.endMeasureIndex) } : fields;
    }

    /** A create or update succeeded: the active loop now is that saved loop, as sent. */
    savedAs(id: number, sent: LoopTicks): void {
        if (!this._region) return;
        this._savedLoopId = id;
        this._savedTicks = { startTick: sent.startTick, endTick: sent.endTick };
    }

    /** A saved loop was deleted. Forget it if the active loop came from it. */
    deleted(id: number): void {
        if (this._savedLoopId !== id) return;
        this._savedLoopId = null;
        this._savedTicks = null;
    }

    private _activate(ticks: LoopTicks, savedLoopId: number | null): void {
        this._region = this._regionFor(ticks);
        this._savedLoopId = savedLoopId;
        this._savedTicks = savedLoopId === null ? null : ticks;
        this._emit('activated', { region: this._region, savedLoopId });
    }

    private _barHeardAt(tick: number): number {
        let latest: model.Beat | null = null;
        for (const beat of allBeats(this._score!)) {
            const start = beat.absolutePlaybackStart;
            if (start <= tick && (!latest || start > latest.absolutePlaybackStart)) latest = beat;
        }
        return latest ? latest.voice.bar.index : measureAt(this._score!, tick);
    }

    private _fits(ticks: LoopTicks): boolean {
        const bars = this._score!.masterBars;
        const last = bars[bars.length - 1];
        return ticks.startTick >= 0 && ticks.endTick > ticks.startTick
            && ticks.endTick <= last.start + last.calculateDuration();
    }

    /** New ticks for a drag. A start dragged to or past the end becomes the last beat before the end, and vice versa. */
    private _dragTicks(handle: LoopHandle, beat: model.Beat): LoopTicks {
        const score = this._score!;
        const { startTick, endTick } = this._region!;
        const beats = staffBeats(beat).filter(b => b.displayDuration > 0);
        if (handle === 'start') {
            let tick = beatStartTick(score, beat);
            if (tick >= endTick) {
                const last = [...beats].reverse().find(b => b.absoluteDisplayStart < endTick);
                tick = last ? beatStartTick(score, last) : startTick;
            }
            return { startTick: tick, endTick };
        }
        let tick = beatEndTick(score, beat);
        if (tick <= startTick) {
            const first = beats.find(b => b.absoluteDisplayStart >= startTick);
            tick = first ? beatEndTick(score, first) : endTick;
        }
        return { startTick, endTick: tick };
    }

    private _regionFor(ticks: LoopTicks): LoopRegion {
        const score = this._score!;
        let playbackStartTick = ticks.startTick;
        let lastEnd = ticks.startTick;
        let firstOutOnset = Infinity;
        for (const beat of allBeats(score)) {
            const display = beat.absoluteDisplayStart;
            const playback = beat.absolutePlaybackStart;
            if (inLoop(ticks, beat)) {
                if (!beat.isRest) playbackStartTick = Math.min(playbackStartTick, playback);
                lastEnd = Math.max(lastEnd, playback + beat.playbackDuration);
            } else if (display >= ticks.endTick && !beat.isRest) {
                firstOutOnset = Math.min(firstOutOnset, playback);
            }
        }
        return {
            startTick: ticks.startTick,
            endTick: ticks.endTick,
            startMeasureIndex: measureAt(score, ticks.startTick),
            endMeasureIndex: measureAt(score, ticks.endTick - 1),
            playbackStartTick,
            playbackEndTick: Math.min(lastEnd, firstOutOnset),
        };
    }

    private _fields(): SavedLoopFields | null {
        if (!this._region) return null;
        const { startMeasureIndex, endMeasureIndex, startTick, endTick } = this._region;
        return { startMeasureIndex, endMeasureIndex, startTick, endTick };
    }

    private _emit<K extends keyof ActiveLoopEvents>(name: K, event: ActiveLoopEvents[K]): void {
        for (const listener of [...(this._listeners[name] ?? [])] as Listener<K>[]) listener(event);
    }
}

function* allBeats(score: model.Score): Iterable<model.Beat> {
    for (const track of score.tracks)
        for (const staff of track.staves)
            for (const bar of staff.bars)
                for (const voice of bar.voices)
                    yield* voice.beats;
}

/** Every beat of the dragged beat's voice on its staff, in order. */
function staffBeats(beat: model.Beat): model.Beat[] {
    const voiceIndex = beat.voice.index;
    return beat.voice.bar.staff.bars.flatMap(bar => bar.voices[voiceIndex]?.beats ?? []);
}
